import type { UseQueryResult } from '@tanstack/react-query';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';

import { useApplicationContext } from '~/shared/context/use-application-context';
import { is403Error } from '~/shared/error-code/error-code';

import type { PositionBootstrap } from '../../data-contract/position-management';
import { canEnterPositions, canReadSomePositions } from '../../data-contract/position-management';
import { unwrapResult } from '../../fetchers/unwrap-result';
import { useApplicationFetchers } from '../../fetchers/use-application-fetchers';
import { positionManagementKeys } from './query-keys';

const BOOTSTRAP_STALE_TIME_MS = 5 * 60 * 1000;

export const usePositionBootstrap = ({
    enabled = true,
}: {
    readonly enabled?: boolean;
} = {}): UseQueryResult<PositionBootstrap, Error> => {
    const { fetchPositionBootstrap } = useApplicationFetchers();

    return useQuery({
        enabled,
        queryFn: ({ signal }) => unwrapResult(fetchPositionBootstrap({ signal })),
        queryKey: positionManagementKeys.bootstrap(),
        staleTime: BOOTSTRAP_STALE_TIME_MS,
        throwOnError: false,
    });
};

/** The bootstrap while positions are switched on; with them off nothing is fetched and nothing is pending. */
export const useEnabledPositionBootstrap = (): {
    readonly bootstrap: PositionBootstrap | undefined;
    readonly error: Error | null;
    readonly isPending: boolean;
} => {
    const {
        applicationState: { featureFlagService },
    } = useApplicationContext();
    const enabled = featureFlagService.isFeatureFlagEnabled('position-management-v-2');
    const { data, error, isPending } = usePositionBootstrap({ enabled });

    return enabled ? { bootstrap: data, error, isPending } : { bootstrap: undefined, error: null, isPending: false };
};

export const useCanEnterPositions = (): boolean => {
    const { bootstrap } = useEnabledPositionBootstrap();

    return bootstrap !== undefined && canEnterPositions(bootstrap.capabilities);
};

/** Whether the user may read some positions, which a transformation lead may without entering Position Management. */
export const useCanReadPositions = (): boolean => {
    const { bootstrap } = useEnabledPositionBootstrap();

    return bootstrap !== undefined && canReadSomePositions(bootstrap);
};

type DefaultArchitectureAccess = 'allowed' | 'denied' | 'failed' | 'pending';

export const useDefaultArchitectureAccess = (): DefaultArchitectureAccess => {
    const { bootstrap, error, isPending } = useEnabledPositionBootstrap();

    if (isPending) return 'pending';

    if (bootstrap !== undefined) return bootstrap.capabilities.can_edit ? 'allowed' : 'denied';

    return error === null || is403Error(error) ? 'denied' : 'failed';
};

export const useGlobalPositionAccess = (): { readonly editGlobal: boolean; readonly readGlobal: boolean } => {
    const capabilities = usePositionBootstrap().data?.capabilities;

    return { editGlobal: capabilities?.can_edit === true, readGlobal: capabilities?.can_read === true };
};

/** Replaces the cached bootstrap, e.g. after an architecture was added or removed locally. */
export const useUpdatePositionBootstrap = (): ((
    update: (bootstrap: PositionBootstrap | undefined) => PositionBootstrap | undefined,
) => void) => {
    const queryClient = useQueryClient();

    return useCallback(
        (update) => {
            queryClient.setQueryData(positionManagementKeys.bootstrap(), update);
        },
        [queryClient],
    );
};
